import React, { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/router'
import Icon from 'components/elements/Icon'

import config from 'environment'

import RequestService from 'services/request.service'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_HIGHLIGHT_CARDS } from 'redux/active/actions/types'
import { CardType } from 'types/deck'

import SimpleSpinner from 'components/elements/SimpleSpinner'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './scryfallFilter.module.scss'

type Props = {
  className?: string
  cardsToFilter: CardType[]
  setFilteredCards: (cards: string[]) => void
  filteredCards: string[]
}

const ScryfallFilter = ({ className, cardsToFilter, filteredCards, setFilteredCards }: Props) => {
  const requestId = useRef<string>('')
  const router = useRouter()
  const defaultFilter = `${router.query.filter || ''}`

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [searchValue, setSearchValue] = useState(defaultFilter)

  const inputRef = useRef<any>(null)

  // Writing the actual asyn part of this function so that we can properly debounce the input onChange
  const scryfallSearch = async (scryQuery: string, uids: string[]) => {
    RequestService.post('/api/cards/scryfall-filter-passthrough/', { q: scryQuery, uids })
      .then(({ uids }) => {
        const matchingCardNames = cardsToFilter.filter(c => uids.includes(c.uid)).map(c => c.name)

        // Because our input can be emptied out before the request resolves, we account for that by throwing out the results if the user deleted their input before we get a response
        // The reason this happens is because we don't send requests for queries with less than 2 characters
        // If we did, we could just update them in the order recieved.
        if (searchValue.length < 2) setFilteredCards([])
        else setFilteredCards(matchingCardNames)
      })
      .catch(err => {
        setError(err)

        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const localSearch = (search: string) => {
    const cardNames = cardsToFilter.map(c => c.name)

    const filteredCards = cardNames.filter(c => c.toLocaleLowerCase().includes(search.toLocaleLowerCase()))

    setLoading(false)
    setError(null)
    setFilteredCards(filteredCards)
  }

  const handleSearch = (value: string) => {
    // If the input doesn't inlcude
    if (!value.match(/[<>:=()]/g)) return localSearch(value)

    const cardUids = cardsToFilter.map(c => c.uid)

    scryfallSearch(value, cardUids)
  }

  useEffect(() => {
    if (searchValue.length < 2) {
      setFilteredCards([])
      setLoading(false)
      setError(null)

      return () => null
    }

    // We're safe to set these here, becasue the debounce will eventually be called and eventually finish, turning these to what they should be once the search ends
    setLoading(true)
    setError(null)

    const debouncedSearch = setTimeout(() => handleSearch(searchValue), 500)

    return () => clearTimeout(debouncedSearch)
  }, [searchValue])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const controlKey = config.getOnMac() ? e.metaKey : e.ctrlKey

      if (basicKeybindSkips(e)) return
      if (e.key === '/' && controlKey) {
        inputRef.current.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Helper component to keep logic a bit simpler in the render
  const MetaInfo = () => {
    if (loading) return null
    if (searchValue.length < 2) return null
    if (error) return <>Error searching</>

    if (!filteredCards.length || searchValue.length >= 2)
      return (
        <button onClick={() => setSearchValue('')} className={styles.clear}>
          <Icon name="close" /> {!filteredCards.length ? 'No results found' : 'Clear filter'}
        </button>
      )

    return null
  }

  // We do this so we aren't opening the search by mistake
  // I don't like that we need this in this component, feels like a separation issue tbh
  const handleIgnoreEnter = (e: React.KeyboardEvent) => (e.key === 'Enter' ? e.preventDefault() : null)
  const cardsInFilterSet = cardsToFilter.length

  return (
    <div className={`${styles.container} ${className}`}>
      <label className={styles.filterHeader}>
        <span className={styles.infoContainer}>
          <span>Syntax filter </span>{' '}
          <a href="https://scryfall.com/docs/syntax" target="_blank" rel="noopener noreferrer">
            <Icon style={{ fontSize: '1rem' }} name="info circle" />
          </a>
        </span>

        <MetaInfo />
      </label>
      <input
        ref={inputRef}
        className={styles.input}
        autoCorrect="off"
        autoComplete="off"
        onFocus={() => inputRef.current.select()}
        placeholder={cardsInFilterSet > 200 ? 'Filter deck (eg: Sol Ring)' : 'Filter deck (eg: o:haste c:red)'}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
        onKeyDown={handleIgnoreEnter}
        value={searchValue}
        disabled={!!router.query?.filter}
      />
      {!loading && <Icon name="search" className={styles.searchIcon} />}
      {loading && <SimpleSpinner size="medXSmall" className={styles.loader} />}
    </div>
  )
}

export default ScryfallFilter
